import { useQueryNormalizer } from '@normy/react-query';
import {
  type MutateOptions,
  useMutation,
  type UseMutationOptions,
} from '@tanstack/react-query';
import { gql } from 'graphql-request';
import isEmpty from 'lodash/isEmpty';
import omit from 'lodash/omit';
import { useCallback } from 'react';
import { TIME_ZONE_UTC } from '@/constants';
import { toEventMetadataInput } from '@/features/eventMetadata/lib/eventMetadataInput.lib';
import { useTriggerAutoSyncTip } from '@/features/interactions/hooks/useAutoSyncTip';
import { hasAnyAutoUpdateExport } from '@/features/interactions/lib/exportStatus.lib';
import { gqlClient, queryClient } from '@/lib';
import type { EventFor, Exact, UpdateEventInput } from '@/types/gql.generated';
import { datePartInZone } from '@/utils/dates';
import type { QueryError } from '@/utils/errors';
import {
  createEventMessagesQueryKey,
  createEventMetadataQueryKey,
  createExperienceEventMessagesQueryKey,
} from '@/utils/queryKeys';
import { ExperienceEventFragment } from '../fragments/ExperienceEventFragment';
import type { ExperienceEventFragment as ExperienceEventFragmentType } from '../fragments/ExperienceEventFragment.generated';
import { hasSameTiming } from '../lib/event.lib';
import type { DecoratedEvent } from '../types';
import type {
  UpdateEventMutation,
  UpdateEventMutationVariables,
} from './useUpdateEvent.generated';

type InternalUpdateEventVariables = {
  input: UpdateEventInput;
  _fragmentMetadata?: ExperienceEventFragmentType['metadata'];
};

// What onMutate hands to onSuccess — named so onSuccess can read the pre-save
// event back off it.
type UpdateEventContext = {
  optimisticData: ExperienceEventFragmentType;
  rollbackData: ExperienceEventFragmentType;
};

const query = gql`
  ${ExperienceEventFragment}
  mutation UpdateEvent($input: UpdateEventInput!) {
    updateEvent(input: $input) {
      ...ExperienceEvent
    }
  }
`;

export const useUpdateEvent = (
  eventFor: EventFor,
  options?: Pick<
    UseMutationOptions<
      UpdateEventMutation,
      QueryError,
      Exact<{ input: UpdateEventInput }>
    >,
    'onSuccess' | 'onError' | 'onSettled'
  >
) => {
  const queryNormalizer = useQueryNormalizer();
  const triggerAutoSyncTip = useTriggerAutoSyncTip();

  const { mutate, ...rest } = useMutation<
    UpdateEventMutation,
    QueryError,
    InternalUpdateEventVariables,
    // onMutate bails out when the event isn't in the normalized cache.
    UpdateEventContext | undefined
  >({
    ...options,
    mutationFn: ({ _fragmentMetadata: _, ...variables }) =>
      gqlClient.request<UpdateEventMutation, UpdateEventMutationVariables>(
        query,
        variables
      ),
    onSuccess: (data, variables, onMutateResult, context) => {
      if (hasAnyAutoUpdateExport(data.updateEvent)) {
        triggerAutoSyncTip(data.updateEvent.id);
      }
      // A metadata-carrying save supersedes the per-(experience, event)
      // override slot written by useUpdateEventMetadata, so refresh it —
      // otherwise a stale override would shadow the saved metadata wherever
      // the slot is consulted (e.g. card chips).
      if (variables._fragmentMetadata && eventFor.experienceId) {
        queryClient.setQueryData(
          createEventMetadataQueryKey(
            eventFor.experienceId,
            data.updateEvent.id
          ),
          data.updateEvent.metadata
        );
      }
      // Scheduled messages are pinned to an instance, so the server drops or
      // reschedules them when the repeat rule or the start moves. Nothing else
      // tells the client, so refetch rather than leave stale rows on screen.
      // No pre-save event (it wasn't in the normalized cache) means we can't
      // tell, so refetch.
      const before = onMutateResult?.rollbackData;
      if (
        eventFor.experienceId &&
        (!before || !hasSameTiming(before, data.updateEvent))
      ) {
        queryClient.invalidateQueries({
          queryKey: createEventMessagesQueryKey(
            eventFor.experienceId,
            data.updateEvent.id
          ),
        });
        queryClient.invalidateQueries({
          queryKey: createExperienceEventMessagesQueryKey(
            eventFor.experienceId
          ),
        });
      }
      options?.onSuccess?.(
        data,
        { input: variables.input },
        onMutateResult,
        context
      );
    },
    onMutate: (variables) => {
      const { eventId, start, end, ...input } = variables.input;
      const prevValue =
        queryNormalizer.getObjectById<ExperienceEventFragmentType>(eventId);
      if (!prevValue) {
        return;
      }

      // Use the fragment-shaped metadata passed via _fragmentMetadata so the
      // category doesn't flicker back to empty during the optimistic window.
      // Falls back to prevValue for non-metadata edits.
      const optimisticMetadata =
        variables._fragmentMetadata ?? prevValue.metadata;

      const optimisticData: ExperienceEventFragmentType = {
        ...prevValue,
        ...input,
        kind: input.kind ?? prevValue.kind,
        title: input.title ?? prevValue.title ?? '',
        metadata: optimisticMetadata,
        id: variables.input.eventId,
        // The date assignments below write in place, and the shallow spread
        // above would otherwise alias prevValue's objects — mutating the
        // rollback snapshot along with the optimistic one, so a failed mutation
        // would "roll back" to the optimistic dates.
        start: { ...prevValue.start },
        end: { ...prevValue.end },
      };

      if (start?.dateOnly) {
        optimisticData.start.date = start.dateOnly.date;
        optimisticData.start.dateTime = null;
        optimisticData.isAllDay = true;
      } else if (start?.dateTime) {
        optimisticData.start.dateTime = start.dateTime.dateTime;
        optimisticData.start.timeZone = start.dateTime.timeZone;
        optimisticData.start.date = null;
        optimisticData.isAllDay = false;
      }

      if (end?.dateOnly) {
        optimisticData.end.date = end.dateOnly.date;
        optimisticData.end.dateTime = null;
        optimisticData.isAllDay = true;
      } else if (end?.dateTime) {
        optimisticData.end.dateTime = end.dateTime.dateTime;
        optimisticData.end.timeZone = end.dateTime.timeZone;
        optimisticData.end.date = null;
        optimisticData.isAllDay = false;
      }

      return {
        optimisticData,
        rollbackData: prevValue,
      };
    },
  });

  const updateEvent = useCallback(
    (
      eventItem: Partial<DecoratedEvent> & { id: string },
      options?: MutateOptions<
        UpdateEventMutation,
        QueryError,
        InternalUpdateEventVariables
      >
    ): void => {
      const {
        id,
        startDate,
        endDate,
        isAllDay,
        timeZone,
        ...entryWithoutRecurrence
      } = eventItem;

      const start: UpdateEventInput['start'] = {};
      const end: UpdateEventInput['end'] = {};

      if (isAllDay && startDate && endDate) {
        start.dateOnly = {
          date: datePartInZone(startDate, TIME_ZONE_UTC).toISODate(),
        };
        end.dateOnly = {
          date: datePartInZone(endDate, TIME_ZONE_UTC).toISODate(),
        };
      } else if (startDate && endDate && timeZone) {
        start.dateTime = {
          dateTime: startDate.toUTC().toISO(),
          timeZone,
        };
        end.dateTime = {
          dateTime: endDate.toUTC().toISO(),
          timeZone,
        };
      }

      const input: UpdateEventInput = {
        ...omit(
          entryWithoutRecurrence,
          'createdAt',
          'updatedAt',
          'interactionApps',
          'lastEndDate',
          'lastEndDateTime',
          'imageUrl',
          'imageId',
          'origin',
          'isSubscribed',
          'exports',
          'exportedToExperiences',
          'start',
          'end',
          'version',
          'metadata'
        ),
        for: eventFor,
        eventId: id,
      };

      if (!isEmpty(start) && !isEmpty(end)) {
        input.start = start;
        input.end = end;
      }

      // The event category is persisted as part of updateEvent via metadata.
      // Only experience events support metadata on the server; chat/job events
      // do not, so skip metadata for non-experience updates to avoid a 400.
      if (
        'metadata' in eventItem &&
        eventItem.metadata &&
        eventFor.experienceId
      ) {
        // Note `metadata.kind` is distinct from `input.kind` above: that's the
        // event's own kind, this is this experience's override of it. Saving an
        // owned event sends the override as null to clear it (see onKindChange),
        // and emoji splits the same way — see toEventMetadataInput.
        input.metadata = toEventMetadataInput(eventItem.metadata);
      }

      const fragmentMetadata =
        'metadata' in eventItem && eventItem.metadata && eventFor.experienceId
          ? (eventItem.metadata as ExperienceEventFragmentType['metadata'])
          : undefined;

      mutate({ input, _fragmentMetadata: fragmentMetadata }, options);
    },

    [mutate, eventFor]
  );

  return {
    ...rest,
    updateEvent,
  };
};
